import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DraggableWindow from './DraggableWindow';
import CustomSelect from '../CustomSelect';
import { lojasService, transferenciasService } from '../../services/api';

function toYmd(dateStr) {
  // input: YYYY-MM-DD
  if (!dateStr) return '';
  return String(dateStr).replaceAll('-', '');
}

function toYmdSlash(dateStr) {
  // input: YYYY-MM-DD
  if (!dateStr) return '';
  return String(dateStr).replaceAll('-', '/');
}

function formatDateBr(value) {
  if (!value) return '-';
  const str = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
  }
  return str;
}

function money(v) {
  const n = parseFloat(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getPedidoStatusLabel(status) {
  const n = parseInt(status, 10);
  const map = {
    1: 'Pendente',
    2: 'EM ANÁLISE',
    3: 'EM SEPARAÇÃO/ FATURAMENTO',
    4: 'AGUARDANDO EXPEDIÇÃO',
    5: 'ENVIADO',
    6: 'ENTREGUE',
    7: 'AGUARDANDO PAGAMENTO',
    8: 'CANCELADO',
    9: 'AGUARDANDO RETIRADA'
  };
  return map[n] || String(status ?? '-');
}

function getPedidoStatusBadgeStyle(status) {
  const n = parseInt(status, 10);
  if (n === 8) return { background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b' };
  if (n === 6) return { background: '#dcfce7', border: '1px solid #bbf7d0', color: '#166534' };
  if (n === 5) return { background: '#dbeafe', border: '1px solid #bfdbfe', color: '#1e40af' };
  if (n === 7) return { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e' };
  return { background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#374151' };
}

const WindowTransferenciasPendentes = ({ onClose, onMinimize, isMinimized, zIndex, onFocus, userData }) => {
  const [filial, setFilial] = useState(userData.loja_id || 22);
  const [lojas, setLojas] = useState([]);

  const { hoje, seteDiasAtras } = useMemo(() => {
    const formatar = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    const atual = new Date();
    const inicio = new Date(atual);
    inicio.setDate(inicio.getDate() - 7);

    return {
      hoje: formatar(atual),
      seteDiasAtras: formatar(inicio)
    };
  }, []);

  const [dataIni, setDataIni] = useState(seteDiasAtras);
  const [dataFim, setDataFim] = useState(hoje);

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const [pedidosPendentes, setPedidosPendentes] = useState([]);
  const [solicitacoesPendentes, setSolicitacoesPendentes] = useState([]);
  const [clienteretiraNaoFez, setClienteretiraNaoFez] = useState([]);
  const [clienteretiraPendenteEntrega, setClienteretiraPendenteEntrega] = useState([]);
  const [movOutros, setMovOutros] = useState([]);
  const [movR, setMovR] = useState([]);
  const [movP, setMovP] = useState([]);

  useEffect(() => {
    const carregarLojas = async () => {
      try {
        const lojasResponse = await lojasService.getLojas();
        const lojasOrdenadas = (lojasResponse || []).sort((a, b) => a.numero - b.numero);
        setLojas(lojasOrdenadas);
      } catch (err) {
        console.error('Erro ao carregar lojas:', err);
      }
    };
    carregarLojas();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErro('');
    setLoading(true);

    setPedidosPendentes([]);
    setSolicitacoesPendentes([]);
    setClienteretiraNaoFez([]);
    setClienteretiraPendenteEntrega([]);
    setMovOutros([]);
    setMovR([]);
    setMovP([]);

    try {
      if (!filial) {
        setErro('Selecione uma loja');
        return;
      }
      if (!dataIni || !dataFim) {
        setErro('Informe data inicial e final');
        return;
      }

      const dataIniYmd = toYmd(dataIni);
      const dataFimYmd = toYmd(dataFim);
      const dataIniSlash = toYmdSlash(dataIni);
      const dataFimSlash = toYmdSlash(dataFim);

      // Ordem: pedidos -> solicitacoes -> clienteretira -> movimentos
      const rPedidos = await transferenciasService.listarPedidosPendentes({
        filialRetira: filial,
        dataIniYmd,
        dataFimYmd
      });
      setPedidosPendentes(Array.isArray(rPedidos) ? rPedidos : []);

      const rSolic = await transferenciasService.listarSolicitacoesTransferenciaPendentes({
        filialOrigemTransf: filial,
        dataIniYmd,
        dataFimYmd
      });
      setSolicitacoesPendentes(Array.isArray(rSolic) ? rSolic : []);

      const rCr = await transferenciasService.listarClienteRetiraTransfNaoFeita({
        lojaTransf: filial,
        dataIniYmd,
        dataFimYmd
      });
      setClienteretiraNaoFez(Array.isArray(rCr) ? rCr : []);

      const rCrPend = await transferenciasService.listarClienteRetiraPendenteEntrega({
        lojaEntrega: filial,
        dataIniYmd,
        dataFimYmd
      });
      setClienteretiraPendenteEntrega(Array.isArray(rCrPend) ? rCrPend : []);

      const rOutros = await transferenciasService.listarMovTransferencia({
        cdfil: filial,
        dataIniYmdSlash: dataIniSlash,
        dataFimYmdSlash: dataFimSlash,
        tipo: 'OUTROS'
      });
      setMovOutros(Array.isArray(rOutros) ? rOutros : []);

      const rR = await transferenciasService.listarMovTransferencia({
        cdfil: filial,
        dataIniYmdSlash: dataIniSlash,
        dataFimYmdSlash: dataFimSlash,
        tipo: 'R'
      });
      setMovR(Array.isArray(rR) ? rR : []);

      const rP = await transferenciasService.listarMovTransferencia({
        cdfil: filial,
        dataIniYmdSlash: dataIniSlash,
        dataFimYmdSlash: dataFimSlash,
        tipo: 'P'
      });
      setMovP(Array.isArray(rP) ? rP : []);

    } catch (err) {
      console.error('Erro ao buscar pendencias de transferencia:', err);
      setErro('Erro ao buscar pendencias.');
    } finally {
      setLoading(false);
    }
  };

  const Section = ({ title, icon, count, actions, children }) => (
    <div className="transfer-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <h4 className="transfer-section-title" style={{ marginBottom: 0 }}>
          <i className={`fas ${icon}`}></i> {title} <span className="transfer-count">({count})</span>
        </h4>
        {actions ? <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{actions}</div> : null}
      </div>
      {children}
    </div>
  );

  const filiaisDestinoMovR = useMemo(() => {
    const vistos = new Set();
    return (movR || []).filter((item) => {
      const key = String(item.CDFILDEST ?? '');
      if (!key || vistos.has(key)) return false;
      vistos.add(key);
      return true;
    });
  }, [movR]);

  const formatarFilialDestino = useCallback((codigo, nomeBruto) => {
    const codigoFmt = codigo != null ? String(codigo).padStart(2, '0') : '--';
    const nome = String(nomeBruto || '').trim();
    if (!nome) return codigoFmt;
    const nomeUpper = nome.toUpperCase();
    if (nomeUpper.startsWith(`${codigoFmt} -`) || nomeUpper.startsWith(`${codigoFmt}-`) || nomeUpper.startsWith(`${parseInt(codigoFmt, 10)} -`)) {
      return nome;
    }
    return `${codigoFmt} - ${nome}`;
  }, []);

  const imprimirMovRecebimento = useCallback(async ({ docSelecionado = null, filialDestSelecionada = null, cortarPorPedido = false } = {}) => {
    try {
      if (!Array.isArray(movR) || movR.length === 0) {
        alert('Nenhum pedido de transferência para imprimir.');
        return;
      }

      const docs = docSelecionado != null
        ? [parseInt(docSelecionado, 10)].filter((doc) => Number.isFinite(doc))
        : Array.from(new Set(
            movR
              .filter((item) => filialDestSelecionada == null || String(item.CDFILDEST) === String(filialDestSelecionada))
              .map((item) => parseInt(item.DOC, 10))
              .filter((doc) => Number.isFinite(doc))
          ));
      const detalhes = await transferenciasService.listarMovTransferenciaDetalhesRecebimento({
        cdfil: filial,
        docs
      });

      if (!Array.isArray(detalhes) || detalhes.length === 0) {
        alert('Nenhum detalhe encontrado para os pedidos de transferência.');
        return;
      }

      const lojaMap = new Map((lojas || []).map((loja) => [String(parseInt(loja.numero, 10)), loja.nome]));
      const grupos = detalhes.reduce((acc, item) => {
        const doc = String(item.DOC);
        if (!acc[doc]) acc[doc] = [];
        acc[doc].push(item);
        return acc;
      }, {});

      const escapeHtml = (value) => String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;');

      const ordenarPorEndereco = (itens) => [...itens].sort((a, b) => {
        const enderecoA = String(a?.ENDERECO || '').trim().toUpperCase();
        const enderecoB = String(b?.ENDERECO || '').trim().toUpperCase();
        if (!enderecoA && !enderecoB) return String(a?.NOMEPRO || '').localeCompare(String(b?.NOMEPRO || ''), 'pt-BR');
        if (!enderecoA) return 1;
        if (!enderecoB) return -1;
        return enderecoA.localeCompare(enderecoB, 'pt-BR', { numeric: true, sensitivity: 'base' });
      });

      const blocos = Object.entries(grupos).map(([doc, itens]) => {
        const itensOrdenados = ordenarPorEndereco(itens);
        const destino = itens[0]?.CDFILDEST != null ? String(itens[0].CDFILDEST).padStart(2, '0') : '--';
        const nomeDestino = itens[0]?.FILIAL_DEST_NOME || lojaMap.get(String(parseInt(itens[0]?.CDFILDEST, 10))) || '';
        const filialLabel = formatarFilialDestino(destino, nomeDestino);
        const linhas = itensOrdenados.map((item) => {
          const barras = item.BARRA || item.BAR1 || item.BAR2 || '';
          const qtd = parseFloat(item.QTD || 0);
          const qtdFmt = Number.isInteger(qtd) ? String(Math.round(qtd)) : qtd.toFixed(2);
          return `
            <div class="print-item">
              <div class="print-item-title">(${escapeHtml(item.CDPRODU)}) ${escapeHtml(item.NOMEPRO)}</div>
              <div class="print-item-meta-row">
                <div class="print-item-meta">${escapeHtml(barras || 'SEM BARRAS')} - ${escapeHtml(item.ENDERECO || 'Não informado')}</div>
                <div class="print-item-qtd">${escapeHtml(qtdFmt)}UN.</div>
              </div>
            </div>
          `;
        }).join('');

        return `
          <section class="print-doc-block ${cortarPorPedido ? 'with-cut' : ''}">
            <div class="print-doc-header">
              <div class="print-doc-number">Pedido ${escapeHtml(doc)}</div>
              <div class="print-doc-filial">${escapeHtml(filialLabel)}</div>
            </div>
            <div class="print-items">${linhas}</div>
            <div class="print-cut-line">${cortarPorPedido ? '----- corte aqui -----' : ''}</div>
          </section>
        `;
      }).join('');

      const printWindow = window.open('', '_blank', 'width=1100,height=800');
      if (!printWindow) {
        alert('Não foi possível abrir a janela de impressão.');
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Pedidos de Transferência</title>
            <link rel="preconnect" href="https://fonts.googleapis.com">
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
            <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap">
            <style>
              @page { size: 80mm auto; margin: 0; }
              * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              html, body { background: #fff; }
              body {
                font-family: 'Quicksand', Arial, Helvetica, sans-serif;
                width: 100%;
                max-width: 60mm;
                margin: 0;
                padding: 0 4mm 0 4mm;
                color: #000;
                font-size: 11px;
                text-rendering: geometricPrecision;
              }
              h1 { margin: 0 0 8px; font-size: 15px; font-weight: 700; text-align: center; color: #000; letter-spacing: 0.2px; }
              .print-doc-block { margin-bottom: 10px; page-break-inside: avoid; }
              .print-doc-block.with-cut { page-break-after: always; }
              .print-doc-block.with-cut:last-of-type { page-break-after: auto; }
              .print-doc-header { display: block; margin-bottom: 7px; line-height: 1.2; }
              .print-doc-number { font-size: 15px; font-weight: 700; text-align: center; color: #000; letter-spacing: 0.15px; }
              .print-doc-filial { margin-top: 3px; font-size: 11px; font-weight: 600; text-align: center; color: #000; letter-spacing: 0.1px; }
              .print-items { display: block; border-top: 1px solid #000; }
              .print-item { padding: 5px 0 4px; border-bottom: 1px solid #000; }
              .print-item:last-child { border-bottom: 1px solid #000; }
              .print-item-title { font-size: 10px; font-weight: 700; line-height: 1.2; color: #000; word-break: break-word; overflow-wrap: anywhere; }
              .print-item-meta-row { margin-top: 2px; display: flex; align-items: flex-start; justify-content: space-between; gap: 6px; padding-right: 1mm; }
              .print-item-meta { flex: 1; min-width: 0; font-size: 9px; line-height: 1.2; color: #000; word-break: break-word; overflow-wrap: anywhere; }
              .print-item-qtd { flex-shrink: 0; font-size: 8px; line-height: 1.2; color: #000; font-weight: 700; text-align: right; white-space: nowrap; padding-right: 1mm; }
              .print-cut-line { margin-top: 8px; font-size: 9px; font-weight: 700; text-align: center; color: #000; letter-spacing: 0.4px; }
              @media print {
                html, body { margin: 0; }
                body { width: 100%; max-width: none; padding: 0 4mm 0 4mm; }
              }
            </style>
          </head>
          <body>
            <h1>Pedidos de Transferência</h1>
            ${blocos}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();

      // A Quicksand vem por <link> na janela nova, que não herda os estilos do app.
      // Sem esperar o download, print() dispara antes e o texto sai em Arial.
      // O timeout evita travar a impressão se a CDN de fontes estiver inacessível
      // (offline / rede da loja bloqueada) — nesse caso cai no fallback e imprime.
      try {
        await Promise.race([
          printWindow.document.fonts?.ready ?? Promise.resolve(),
          new Promise((resolve) => setTimeout(resolve, 2000)),
        ]);
      } catch (_) { /* fonte indisponível: segue com o fallback */ }

      printWindow.print();
    } catch (err) {
      console.error('Erro ao imprimir pedidos de transferência:', err);
      alert('Erro ao montar impressão dos pedidos de transferência.');
    }
  }, [filial, formatarFilialDestino, lojas, movR]);

  return (
    <DraggableWindow
      title="Pedidos e Transferencias Pendentes"
      icon="fa-right-left"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={1050}
      initialTop={70}
      initialLeft={260}
      maxHeight="88vh"
      className="transferencias-pendentes-window"
    >
      <form onSubmit={handleSubmit} className="consulta-form">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Loja</label>
            <CustomSelect
              options={lojas.map((loja) => ({ value: loja.numero, label: `${loja.numero} - ${loja.nome}` }))}
              value={filial}
              onChange={setFilial}
              placeholder="Selecione a loja..."
              searchPlaceholder="Buscar loja..."
            />
          </div>

          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Data inicial</label>
            <input type="date" value={dataIni} onChange={(e) => setDataIni(e.target.value)} />
          </div>

          <div className="consulta-form-group" style={{ marginBottom: 0 }}>
            <label>Data final</label>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
          </div>

          <button type="submit" className="consulta-btn-submit" disabled={loading} style={{ height: '46px' }}>
            <i className="fas fa-sync"></i> Carregar
          </button>
        </div>
      </form>

      {erro && <div className="consulta-error" style={{ marginTop: 12 }}>{erro}</div>}

      {loading && (
        <div className="consulta-loading" style={{ marginTop: 12 }}>
          <i className="fas fa-spinner fa-spin"></i> Buscando pendencias...
        </div>
      )}

      {!loading && (
        <>
          {pedidosPendentes.length > 0 && (
          <Section title="Pedidos de Clientes" icon="fa-file-invoice" count={pedidosPendentes.length}>
            <div className="transfer-table-container">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Cliente</th>
                    <th>Nome</th>
                    <th>Status</th>
                    <th>Entrega</th>
                    <th className="text-right">Total</th>
                    <th>Data</th>
                    <th>Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidosPendentes.map((r, idx) => (
                    <tr key={`${r.pedido}-${idx}`}>
                      <td className="is-strong">{r.pedido}</td>
                      <td>{r.cliente}</td>
                      <td>{r.nome}</td>
                      <td>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '3px 8px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 800,
                            ...getPedidoStatusBadgeStyle(r.STATUS)
                          }}
                          title={`Status ${r.STATUS}`}
                        >
                          {getPedidoStatusLabel(r.STATUS)}
                        </span>
                      </td>
                      <td>{r.ENTREGA}</td>
                      <td className="text-right">{money(r.VLRTOTAL)}</td>
                      <td>{r.data}</td>
                      <td>{r.hora}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          )}

          {solicitacoesPendentes.length > 0 && (
          <Section title="Solicitações" icon="fa-truck-fast" count={solicitacoesPendentes.length}>
            <div className="transfer-table-container">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>Loja</th>
                    <th>Cliente</th>
                    <th>Nome</th>
                    <th>Entrega</th>
                    <th className="text-right">Total</th>
                    <th>Data</th>
                    <th>Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {solicitacoesPendentes.map((r, idx) => (
                    <tr key={`${r.pedido}-${idx}`}>
                      <td className="is-strong">{r.pedido}</td>
                      <td>{r.LOJA}</td>
                      <td>{r.cliente}</td>
                      <td>{r.nome}</td>
                      <td>{r.ENTREGA}</td>
                      <td className="text-right">{money(r.VLRTOTAL)}</td>
                      <td>{r.data}</td>
                      <td>{r.hora}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          )}

          {clienteretiraNaoFez.length > 0 && (
          <Section title="Cliente Retira sem transferência" icon="fa-user-clock" count={clienteretiraNaoFez.length}>
            <div className="transfer-table-container">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>CPF</th>
                    <th>Filial</th>
                    <th>Produto</th>
                    <th className="text-right">Qtd</th>
                    <th>Data</th>
                    <th>Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {clienteretiraNaoFez.map((r, idx) => (
                    <tr key={`${r.pedido}-${idx}`}>
                      <td className="is-strong">{r.pedido}</td>
                      <td className="mono">{r.cpf}</td>
                      <td>{r.CDFIL}</td>
                      <td>{r.NOMEPRO}</td>
                      <td className="text-right">{parseFloat(r.QTD || 0).toFixed(0)}</td>
                      <td>{r.data}</td>
                      <td>{r.hora}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          )}

          {clienteretiraPendenteEntrega.length > 0 && (
          <Section title="Cliente Retira pendente entregar" icon="fa-hand" count={clienteretiraPendenteEntrega.length}>
            <div className="transfer-table-container">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Pedido</th>
                    <th>CPF</th>
                    <th>Entrega</th>
                    <th>Origem</th>
                    <th>Produto</th>
                    <th className="text-right">Qtd</th>
                    <th>Data</th>
                    <th>Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {clienteretiraPendenteEntrega.map((r, idx) => (
                    <tr key={`${r.pedido}-${idx}`}>
                      <td className="is-strong">{r.pedido}</td>
                      <td className="mono">{r.cpf}</td>
                      <td>{r.LJENTREGA}</td>
                      <td>{r.CDFIL}</td>
                      <td>{r.NOMEPRO}</td>
                      <td className="text-right">{parseFloat(r.QTD || 0).toFixed(0)}</td>
                      <td>{r.data}</td>
                      <td>{r.hora}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          )}

          {movOutros.length > 0 && (
          <Section title="Mov Transferência (TIPO != R/P)" icon="fa-right-left" count={movOutros.length}>
            <div className="transfer-table-container">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Doc</th>
                    <th>Tipo</th>
                    <th>Destinatário</th>
                    <th>Produto</th>
                    <th className="text-right">Qtd</th>
                  </tr>
                </thead>
                <tbody>
                  {movOutros.map((r, idx) => (
                    <tr key={`${r.DOC}-${idx}`}>
                      <td>{formatDateBr(r.DATA)}</td>
                      <td className="is-strong">{r.DOC}</td>
                      <td>{r.TIPO}</td>
                      <td>{formatarFilialDestino(r.CDFILDEST, lojas.find((loja) => String(parseInt(loja.numero, 10)) === String(parseInt(r.CDFILDEST, 10)))?.nome || '')}</td>
                      <td>{r.NOMEPRO}</td>
                      <td className="text-right">{parseFloat(r.QTD || 0).toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          )}

          {movR.length > 0 && (
          <Section
            title="Mov Transferência (TIPO = R)"
            icon="fa-ban"
            count={movR.length}
            actions={
              <>
                <button
                  type="button"
                  onClick={() => imprimirMovRecebimento({ cortarPorPedido: true })}
                  disabled={movR.length === 0}
                  className="consulta-btn-submit"
                  style={{ width: 'auto', padding: '8px 12px', opacity: movR.length === 0 ? 0.6 : 1 }}
                >
                  <i className="fas fa-layer-group"></i> Imprimir todos
                </button>
                {filiaisDestinoMovR.map((item) => (
                  <button
                    key={`filial-print-${item.CDFILDEST}`}
                    type="button"
                    onClick={() => imprimirMovRecebimento({ filialDestSelecionada: item.CDFILDEST, cortarPorPedido: false })}
                    className="consulta-btn-submit"
                    style={{ width: 'auto', padding: '8px 12px' }}
                  >
                    <i className="fas fa-print"></i> Filial {String(item.CDFILDEST).padStart(2, '0')}
                  </button>
                ))}
              </>
            }
          >
            <div className="transfer-table-container">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Doc</th>
                    <th>Destinatário</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {movR.map((r, idx) => (
                    <tr key={`${r.DOC}-${idx}`}>
                      <td>{formatDateBr(r.DATA)}</td>
                      <td className="is-strong">{r.DOC}</td>
                      <td>{formatarFilialDestino(r.CDFILDEST, lojas.find((loja) => String(parseInt(loja.numero, 10)) === String(parseInt(r.CDFILDEST, 10)))?.nome || '')}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => imprimirMovRecebimento({ docSelecionado: r.DOC, cortarPorPedido: false })}
                          className="consulta-btn-submit"
                          style={{ width: 'auto', height: '32px', padding: '0 10px', fontSize: '12px' }}
                        >
                          <i className="fas fa-print"></i> Imprimir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          )}

          {movP.length > 0 && (
          <Section title="Mov Transferência (TIPO = P)" icon="fa-circle-check" count={movP.length}>
            <div className="transfer-table-container">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Doc</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {movP.map((r, idx) => (
                    <tr key={`${r.DOC}-${idx}`}>
                      <td className="is-strong">{r.DOC}</td>
                      <td>{String(r.DATA).slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
          )}
        </>
      )}
    </DraggableWindow>
  );
};

export default WindowTransferenciasPendentes;
